import { describeApiError, type RoomOperator } from '@brush/api-client';
import { ErrorNotice, useDebouncedValue } from '@brush/ui';
import { Check, ExternalLink, Pencil, RotateCcw, Search, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  MAX_STAGED,
  nameProblem,
  noMatchesText,
  roleDisplay,
  roleNoticeText,
  searchHint,
  stage,
  stagingProblem,
  unstage,
  type Candidate,
} from '../operators/assign';
import {
  isRemoved,
  isRenamed,
  linkedObjectIds,
  nameOf,
  withRename,
  type PendingOperators,
} from '../operators/pending';
import {
  ROLE_GRANT_LINK_TITLE,
  ROLE_GRANT_PATH,
  ROLE_GRANT_SCOPE_NOTE,
  offersRoleGrantLink,
} from '../operators/portal';
import { useDirectorySearch } from '../queries';

const inputClass =
  'w-full rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-950 outline-none focus:border-felt-500';

/**
 * The server's own minimum, mirrored so a one-character search is not sent at all. The API refuses
 * it with a sentence, which is the control; this only avoids spending a round trip — and a Graph
 * throttle allowance shared by every administrator — to be told what the screen already knew.
 */
const MIN_QUERY = 2;

/** Long enough that typing a name costs one search rather than six. */
const SEARCH_DEBOUNCE_MS = 400;

const toneClass = {
  ok: 'bg-felt-700/10 text-felt-900',
  warn: 'bg-chip-500/10 text-chip-500',
  muted: 'bg-ink-800/5 text-ink-800/60',
} as const;

function RoleBadge({ status, portalUrl }: { status: string; portalUrl: string | null }) {
  const display = roleDisplay(status);

  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <span
        title={display.detail}
        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${toneClass[display.tone]}`}
      >
        {display.label}
      </span>
      {portalUrl && offersRoleGrantLink(status) ? (
        <a
          href={portalUrl}
          target="_blank"
          rel="noreferrer noopener"
          title={ROLE_GRANT_LINK_TITLE}
          className="inline-flex shrink-0 items-center gap-0.5 text-[10px] text-felt-700 underline underline-offset-2 transition hover:text-felt-900"
        >
          Grant in Entra
          <ExternalLink className="size-2.5" aria-hidden="true" />
        </a>
      ) : null}
    </span>
  );
}

/**
 * Who may operate this room, inside the dialog that owns the room.
 *
 * <p>
 * <b>There is no room picker, because the room is the dialog's.</b> That is most of what moving
 * this out of its own panel bought: the panel's first act was asking which room, and every rule in
 * it — who is already an operator, which names are this room's — was scoped by an answer the
 * operator had to give twice.
 * </p>
 * <p>
 * <b>Nothing here writes.</b> Every gesture records an intention in {@link PendingOperators}, and
 * the dialog's one Save is what applies them in order. That is what lets a CREATE dialog stage
 * people against a room that does not exist yet, and what makes Cancel able to discard the lot.
 * </p>
 */
export function OperatorsSection({
  roomId,
  links,
  pending,
  onChange,
  portalUrl,
  roleNotice,
  readOnly,
}: {
  /** Null while the room is being registered — there is nothing to be already linked to. */
  roomId: string | null;
  links: readonly RoomOperator[];
  pending: PendingOperators;
  onChange: (next: PendingOperators) => void;
  portalUrl: string | null;
  roleNotice: string | null;
  readOnly: boolean;
}) {
  const [query, setQuery] = useState('');
  const [stagingNotice, setStagingNotice] = useState<string | null>(null);

  const debounced = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const searchable = debounced.length >= MIN_QUERY;
  const search = useDirectorySearch(debounced, searchable);

  // Already an operator HERE — the room's own knowledge rather than the directory's, so it is
  // joined on the client. Pending removals are excluded by `linkedObjectIds`, which is what makes
  // remove-then-re-add-under-a-new-name a single Save rather than two.
  const linkedHere = useMemo(
    () => (roomId ? linkedObjectIds(links, roomId, pending) : new Set<string>()),
    [links, roomId, pending],
  );

  const results: Candidate[] = useMemo(
    () =>
      (search.data?.data ?? []).map((person) => ({
        ...person,
        alreadyLinked: linkedHere.has(person.objectId),
      })),
    [search.data, linkedHere],
  );

  const searchError = describeApiError(search.error);
  const searchRoleNotice = roleNoticeText(search.data?.roleNotice, 'search');

  // WHICH DIRECTORY ANSWERED, latched. The flag rides on a search answer, so each new term leaves
  // `search.data` undefined for the length of its request — and without the latch the hint would
  // blink off and on as somebody types, which reads as a screen that cannot make up its mind.
  const [answeredFake, setAnsweredFake] = useState(false);
  useEffect(() => {
    if (search.data?.isFakeDirectory) setAnsweredFake(true);
  }, [search.data?.isFakeDirectory]);

  const isFakeDirectory = search.data?.isFakeDirectory === true || answeredFake;
  const directoryHint = searchHint(isFakeDirectory);

  const addPerson = (person: Candidate) => {
    const problem = stagingProblem(pending.staged, person);

    // A REASON rather than a click that does nothing. "Already staged", "already an operator" and
    // "the list is full" all leave the list the same length from the outside.
    setStagingNotice(problem);
    if (problem === null) onChange({ ...pending, staged: stage(pending.staged, person) });
  };

  const here = roomId ? links.filter((l) => l.roomId === roomId) : [];

  return (
    <section className="border-t border-ink-800/10 pt-4">
      <h4 className="text-sm font-medium text-ink-950">Operators</h4>
      <p className="mt-1 text-xs text-ink-800/60">
        Who may manage this room. An operator can run several rooms; they are added once per room.
        Platform administrators reach every room without being added.
      </p>

      {/*
        The two grants, said where somebody is about to make one of them. Adding people got much
        easier and granting the role did not, so the population of half-configured operators goes
        up unless this is on the screen — and the scope sentence matters more here than it did in
        the panel, because every other control in this dialog is about one room.
      */}
      <p className="mt-2 rounded-lg bg-ink-800/5 px-3 py-2 text-[11px] leading-relaxed text-ink-800/70">
        <strong className="font-medium text-ink-950">Two grants, and both are needed.</strong>{' '}
        Adding somebody here says <em>which rooms</em> they may manage. The <em>Room operator</em>{' '}
        app role in Entra says whether they may manage anything at all, and is granted separately
        under <span className="font-mono">{ROLE_GRANT_PATH}</span>. The status beside each person is
        that role. {ROLE_GRANT_SCOPE_NOTE}
        {portalUrl ? (
          <>
            {' '}
            <a
              href={portalUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-0.5 font-medium text-felt-700 underline underline-offset-2 transition hover:text-felt-900"
            >
              Open the Microsoft Entra admin center
              <ExternalLink className="size-3" aria-hidden="true" />
            </a>
            , in a new tab.
          </>
        ) : null}
      </p>

      {/* ---- find somebody ------------------------------------------------------------ */}

      <label className="mt-3 block">
        <span className="mb-1 block text-xs text-ink-800/60">Find somebody</span>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-ink-800/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={readOnly}
            placeholder="Name or work address"
            className={`${inputClass} pl-8 disabled:bg-ink-800/5`}
          />
        </div>
      </label>

      {/*
        Not an ErrorNotice and not a warning tone: nothing has failed, and a red box would say the
        search had. It renders nothing at all in every deployed environment.
      */}
      {directoryHint ? (
        <p className="mt-3 rounded-lg bg-ink-800/5 px-3 py-2 text-[11px] leading-relaxed text-ink-800/70">
          {directoryHint}
        </p>
      ) : null}

      {searchable ? (
        <div className="mt-3">
          {/*
            The SEARCH failed: nothing to show. describeApiError carries the API's own wording for a
            refused query and for our own rate limit alike, so there is nothing to reword here.
          */}
          <ErrorNotice message={searchError} className="mb-3" />

          {search.isPending ? (
            <p className="text-xs text-ink-800/50">Searching…</p>
          ) : searchError ? null : results.length === 0 ? (
            <p className="text-xs text-ink-800/50">{noMatchesText(debounced, isFakeDirectory)}</p>
          ) : (
            <>
              {/*
                The ROLE READ failed, or a group holds the role. Either way the results below are
                real and complete, so this sits above them as a note rather than replacing them.
              */}
              {searchRoleNotice ? (
                <p className="mb-3 rounded-lg bg-ink-800/5 px-3 py-2 text-[11px] leading-relaxed text-ink-800/70">
                  {searchRoleNotice}
                </p>
              ) : null}

              <ul className="space-y-1.5">
                {results.map((person) => (
                  <li
                    key={person.objectId}
                    className="flex items-center justify-between gap-3 rounded-lg border border-ink-800/10 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm text-ink-950">{person.displayName}</span>
                        <RoleBadge status={person.roomRole} portalUrl={portalUrl} />
                      </div>
                      {/* A name is not an identity — 188 names in the WSOP roster are shared. */}
                      <div className="truncate text-[11px] text-ink-800/50">
                        {person.mail ?? person.userPrincipalName}
                      </div>
                    </div>

                    {person.alreadyLinked ? (
                      <span className="shrink-0 text-[11px] text-ink-800/50">
                        Already an operator
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => addPerson(person)}
                        disabled={readOnly}
                        className="shrink-0 rounded-lg border border-ink-800/15 px-2.5 py-1 text-xs text-ink-950 transition hover:border-felt-500 disabled:opacity-40"
                      >
                        Add
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : null}

      {stagingNotice ? <p className="mt-2 text-xs text-chip-500">{stagingNotice}</p> : null}

      {/* ---- staged, and then who already operates it ---------------------------------- */}

      {pending.staged.length > 0 ? (
        <div className="mt-4 rounded-lg border border-felt-500/30 bg-felt-700/5 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-ink-950">
              Adding {pending.staged.length} on save
            </span>
            <span className="text-[11px] text-ink-800/50">
              {pending.staged.length} of {MAX_STAGED}
            </span>
          </div>

          <ul className="space-y-1.5">
            {pending.staged.map((entry) => {
              const problem = nameProblem(entry.name);

              return (
                <li key={entry.person.objectId} className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 flex items-center gap-2">
                      <span className="truncate text-[11px] text-ink-800/60">
                        {entry.person.mail ?? entry.person.userPrincipalName}
                      </span>
                      <RoleBadge status={entry.person.roomRole} portalUrl={portalUrl} />
                    </div>
                    <input
                      value={entry.name}
                      onChange={(e) =>
                        onChange({
                          ...pending,
                          staged: pending.staged.map((s) =>
                            s.person.objectId === entry.person.objectId
                              ? { ...s, name: e.target.value }
                              : s,
                          ),
                        })
                      }
                      aria-label={`Name for ${entry.person.displayName}`}
                      className={`${inputClass} ${problem ? 'border-chip-500' : ''}`}
                    />
                    {/*
                      An over-length prefill is FLAGGED, never truncated — the one place the
                      administrator is asked to shorten a name rather than having it silently done.
                    */}
                    {problem ? <p className="mt-0.5 text-[11px] text-chip-500">{problem}</p> : null}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      onChange({ ...pending, staged: unstage(pending.staged, entry.person.objectId) })
                    }
                    aria-label={`Remove ${entry.person.displayName}`}
                    className="mt-4 shrink-0 text-ink-800/40 transition hover:text-chip-500"
                  >
                    <X className="size-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {roleNotice ? (
        <p className="mt-4 rounded-lg bg-ink-800/5 px-3 py-2 text-[11px] leading-relaxed text-ink-800/70">
          {roleNotice}
        </p>
      ) : null}

      {roomId === null ? (
        <p className="mt-4 text-xs text-ink-800/50">
          Anybody added here is linked when the room is registered.
        </p>
      ) : here.length === 0 ? (
        <p className="mt-4 text-xs text-ink-800/50">
          Nobody operates this room yet. Until somebody does, only platform administrators can
          manage it.
        </p>
      ) : (
        <ul className="mt-4 space-y-1.5">
          {here.map((link) => (
            <LinkRow
              key={link.id}
              link={link}
              pending={pending}
              onChange={onChange}
              portalUrl={portalUrl}
              readOnly={readOnly}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One existing link, and whatever the dialog is holding against it.
 *
 * <p>
 * <b>A removal is marked rather than hidden, and can be undone.</b> Hiding it would make Cancel the
 * only way back from a mis-click — which discards everything else in the dialog with it, and is
 * therefore the more expensive of the two mistakes.
 * </p>
 */
function LinkRow({
  link,
  pending,
  onChange,
  portalUrl,
  readOnly,
}: {
  link: RoomOperator;
  pending: PendingOperators;
  onChange: (next: PendingOperators) => void;
  portalUrl: string | null;
  readOnly: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => nameOf(pending, link) ?? '');

  const removed = isRemoved(pending, link.id);
  const renamed = isRenamed(pending, link);
  const problem = nameProblem(draft);

  const save = () => {
    if (problem) return;
    onChange(withRename(pending, link, draft));
    setEditing(false);
  };

  return (
    <li
      className={`flex items-center justify-between gap-3 rounded-lg border border-ink-800/10 px-3 py-2 ${
        removed ? 'opacity-50' : ''
      }`}
    >
      <div className="min-w-0 flex-1">
        {editing ? (
          <>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              // Same validation as staging: one rule about one column, so a name refused on the
              // way in cannot be accepted on the way back.
              aria-label={`Rename ${nameOf(pending, link) ?? 'operator'} for this room`}
              className={`${inputClass} ${problem ? 'border-chip-500' : ''}`}
              autoFocus
            />
            {problem ? <p className="mt-0.5 text-[11px] text-chip-500">{problem}</p> : null}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span
                className={`truncate text-sm text-ink-950 ${removed ? 'line-through' : ''}`}
              >
                {nameOf(pending, link) ?? 'Unnamed operator'}
              </span>
              <RoleBadge status={link.roomRole} portalUrl={portalUrl} />
              {removed ? (
                <span className="shrink-0 text-[11px] text-chip-500">Removed on save</span>
              ) : renamed ? (
                <span className="shrink-0 text-[11px] text-felt-700">Renamed on save</span>
              ) : null}
            </div>
            <div className="truncate font-mono text-[10px] text-ink-800/50">
              {link.userObjectId}
            </div>
          </>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {removed ? (
          <button
            type="button"
            onClick={() =>
              onChange({ ...pending, removals: pending.removals.filter((id) => id !== link.id) })
            }
            aria-label="Keep this operator"
            title="Keep this operator"
            className="text-ink-800/40 transition hover:text-ink-950"
          >
            <RotateCcw className="size-4" />
          </button>
        ) : editing ? (
          <>
            <button
              type="button"
              onClick={save}
              disabled={problem !== null}
              aria-label="Use this name"
              className="text-felt-700 transition hover:text-felt-900 disabled:opacity-40"
            >
              <Check className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(nameOf(pending, link) ?? '');
                setEditing(false);
              }}
              aria-label="Cancel rename"
              className="text-ink-800/40 transition hover:text-ink-950"
            >
              <X className="size-4" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={readOnly}
              // "for this room" is the whole point: the same person at another room keeps their own
              // name, and the label is where an administrator learns that without being told.
              aria-label={`Rename ${nameOf(pending, link) ?? 'operator'} for this room`}
              className="text-ink-800/40 transition hover:text-ink-950 disabled:opacity-40"
            >
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => onChange({ ...pending, removals: [...pending.removals, link.id] })}
              disabled={readOnly}
              aria-label="Remove from this room"
              className="text-ink-800/40 transition hover:text-chip-500 disabled:opacity-40"
            >
              <Trash2 className="size-4" />
            </button>
          </>
        )}
      </div>
    </li>
  );
}
